/**
 * StatusAnnouncer — WCAG 4.1.3 Live-region announcement utility (Admin)
 *
 * Renders two visually-hidden live regions (polite + assertive) at the root
 * of the document. Messages injected here are announced by screen readers
 * WITHOUT stealing keyboard focus.
 *
 * Usage:
 *   1. Render <StatusAnnouncer /> once in ProvidersWrapper or App root.
 *   2. Call `announceStatus(message)` for non-urgent info/success.
 *   3. Call `announceAlert(message)` for urgent errors.
 *
 * Design note: Content-swap trick (clear then set with 50 ms delay) ensures
 * re-announcing the same string is always picked up by AT.
 */
import React, { useEffect, useRef } from 'react';

// ---------------------------------------------------------------------------
// Singleton module-level refs so any code can call announce* without prop drilling
// ---------------------------------------------------------------------------
type AnnounceFn = (message: string) => void;

let _announcePolite: AnnounceFn = () => {};
let _announceAssertive: AnnounceFn = () => {};

/** Announce a non-urgent status (success / info). Uses aria-live="polite". */
export const announceStatus: AnnounceFn = (msg) => _announcePolite(msg);

/** Announce an urgent alert (error / critical warning). Uses aria-live="assertive". */
export const announceAlert: AnnounceFn = (msg) => _announceAssertive(msg);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useStatusAnnouncer() {
  return { announceStatus, announceAlert };
}

// ---------------------------------------------------------------------------
// Component — render ONCE at app root
// ---------------------------------------------------------------------------
const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const StatusAnnouncer: React.FC = () => {
  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    _announcePolite = (message: string) => {
      if (!politeRef.current) return;
      politeRef.current.textContent = '';
      const id = setTimeout(() => {
        if (politeRef.current) politeRef.current.textContent = message;
      }, 50);
      return () => clearTimeout(id);
    };

    _announceAssertive = (message: string) => {
      if (!assertiveRef.current) return;
      assertiveRef.current.textContent = '';
      const id = setTimeout(() => {
        if (assertiveRef.current) assertiveRef.current.textContent = message;
      }, 50);
      return () => clearTimeout(id);
    };

    return () => {
      _announcePolite = () => {};
      _announceAssertive = () => {};
    };
  }, []);

  return (
    <>
      {/* Polite: for success / info — does not interrupt current speech */}
      <div
        ref={politeRef}
        id="admin-status-announcer-polite"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-relevant="additions text"
        style={srOnly}
      />
      {/* Assertive: for errors — interrupts current speech immediately */}
      <div
        ref={assertiveRef}
        id="admin-status-announcer-assertive"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        aria-relevant="additions text"
        style={srOnly}
      />
    </>
  );
};

export default StatusAnnouncer;
